const AvailableSupplier = (data: IAvailableSupplier): IAvailableSupplierModel => {
	const {
		conversion_factor,
		logistic_minimum_amount,
		logistic_minimum_unit,
		supplier_id,
		package_content_net,
		package_unit,
		price_per_unit,
		product_name,
		...rest
	} = data

	return {
		...rest,
		conversionFactor: conversion_factor,
		logisticMinimumAmount: logistic_minimum_amount,
		logisticMinimumUnit: logistic_minimum_unit,
		supplierId: supplier_id,
		packageContentNet: package_content_net,
		packageUnit: package_unit,
		pricePerUnit: price_per_unit,
		productName: product_name,
	}
}

function resolveSupplierProductName(modelData: ISelectedSupplierModel, apiData: ISelectedSupplier) {
	return modelData.supplierProductName ?? apiData.supplier_product_name ?? apiData.product_name ?? null
}

function resolveSupplierIdentity(data: any, modelData: ISelectedSupplierModel, apiData: ISelectedSupplier) {
	return {
		isAccepted: modelData.isAccepted ?? apiData.is_accepted ?? false,
		isProductCompleted: modelData.isProductCompleted ?? apiData.is_product_completed ?? false,
		productName: modelData.productName ?? apiData.product_name ?? undefined,
		supplierId: modelData.supplierId ?? apiData.supplier_id ?? data.id,
		supplierName: modelData.supplierName ?? apiData.supplier_name ?? data.name,
		supplierProductId: modelData.supplierProductId ?? apiData.supplier_product_id ?? null,
		supplierProductName: resolveSupplierProductName(modelData, apiData),
	}
}

function resolveSupplierPricing(modelData: ISelectedSupplierModel, apiData: ISelectedSupplier) {
	return {
		supplierPriceListId: modelData.supplierPriceListId ?? apiData.supplier_price_list_id ?? null,
		unitPrice: modelData.unitPrice ?? apiData.unit_price ?? null,
		activePriceList:
			modelData.activePriceList ??
			(typeof apiData.active_price_list === 'boolean' ? apiData.active_price_list : true),
		orderedQuantity: modelData.orderedQuantity ?? apiData.ordered_quantity ?? null,
		packageContentNet: modelData.packageContentNet ?? apiData.package_content_net ?? null,
		pricePerKg: modelData.pricePerKg ?? apiData.price_per_kg ?? null,
		netValue: modelData.netValue ?? apiData.net_value ?? null,
	}
}

function resolveSupplierLogistic(modelData: ISelectedSupplierModel, apiData: ISelectedSupplier) {
	return {
		logisticMinimumAmount: modelData.logisticMinimumAmount ?? apiData.logistic_minimum_amount ?? null,
		logisticMinimumUnit: modelData.logisticMinimumUnit ?? apiData.logistic_minimum_unit ?? null,
		purchaseOrderSupplierNumberId:
			modelData.purchaseOrderSupplierNumberId ?? apiData.purchase_order_supplier_number_id ?? null,
		supplierOrderNumber: modelData.supplierOrderNumber ?? apiData.supplier_order_number ?? null,
	}
}

const SelectedSupplier = (data: ISelectedSupplier | ISelectedSupplierModel): ISelectedSupplierModel => {
	const apiData = data as ISelectedSupplier
	const modelData = data as ISelectedSupplierModel

	return {
		id: data.id,
		name: data.name,
		source: data.source,
		amount: data.amount ?? null,
		isActive: modelData.isActive ?? (data.amount !== null && data.amount !== undefined && data.amount > 0),
		unit: data.unit ?? null,
		...resolveSupplierIdentity(data, modelData, apiData),
		...resolveSupplierPricing(modelData, apiData),
		...resolveSupplierLogistic(modelData, apiData),
	}
}

const ProductStatus = (data: IPurchaseOrderItemStatus): IPurchaseOrderItemStatusModel => ({
	id: data.id ?? null,
	itemId: data.item_id ?? null,
	reasonCode: data.reason_code ?? null,
	reasonLabel: data.reason_label ?? null,
	reasonNote: data.reason_note ?? null,
	createdAt: data.created_at ?? null,
	supplierOrderSequence: data.supplier_order_sequence ?? null,
	purchaseSupplierOrderId: data.purchase_supplier_order_id ?? data.id ?? null,
	status: data.status ?? null,
})

const ProductWarning = (data: string | IPurchaseOrderItemWarning): { code: string; message: string } => {
	if (typeof data === 'string') {
		return {
			code: data,
			message: purchaseOrderWarningLabels[data] ?? data,
		}
	}

	const code = data.code ?? 'unknown'

	return {
		code,
		message: data.message ?? purchaseOrderWarningLabels[code] ?? code,
	}
}

function resolveProductSupplierName(
	supplierName: string | null | undefined,
	first: ISelectedSupplierModel | undefined,
): string {
	return supplierName ?? first?.supplierName ?? first?.name ?? '-'
}

function resolveProductBaseFields(data: IProductWithSuppliers) {
	return {
		systemName: data.system_name ?? data.name,
		mainName: data.main_name ?? '',
		isAlternative: data.is_alternative ?? false,
		category: data.category ?? '-',
		requiredQuantity: data.required_quantity ?? data.quantity,
		orderedQuantity: data.ordered_quantity ?? 0,
		packageContentNet: data.package_content_net ?? null,
		pricePerKg: data.price_per_kg ?? null,
		netValue: data.net_value ?? 0,
		deliveryDeadline: data.delivery_deadline ?? null,
		warehouseStock: data.warehouse_stock ?? null,
		logisticMinimumAmount: data.logistic_minimum_amount ?? null,
		logisticMinimumUnit: data.logistic_minimum_unit ?? null,
	}
}

function resolveProductAvailableSuppliers(data: IProductWithSuppliers): IAvailableSupplierModel[] {
	return data.available_suppliers && Array.isArray(data.available_suppliers)
		? data.available_suppliers.map(d => AvailableSupplier(d))
		: []
}

function resolveCollectionFields(data: IProductWithSuppliers) {
	return {
		removed: data.removed?.map(s => ProductStatus(s)) ?? [],
		processed: data.processed?.map(s => ProductStatus(s)) ?? [],
		warningCodes: data.warnings?.map(w => ProductWarning(w).code) ?? [],
		warningMessages: data.warnings?.map(w => ProductWarning(w).message) ?? [],
	}
}

// Sama obecność wpisu w `processed` nie oznacza jeszcze, że dostawcy nie da się zmienić —
// pozycja bywa tam już na etapie `request_ready`, czyli zanim ktokolwiek zatwierdzi ofertę.
//
// Świadomie jest to lista dozwolonych, a nie blokujących: brak `status` (starsze API),
// literówka albo nowy stan dorzucony po stronie backendu zostawiają wiersz zablokowany,
// czyli tak jak działa dziś. Odblokowanie wymaga statusu, który znamy i uznaliśmy za bezpieczny.
const SUPPLIER_EDITABLE_STATUSES: string[] = [
	EProductsBySupplierStatus.REQUEST_READY,
	EProductsBySupplierStatus.REQUEST_SENT,
	EProductsBySupplierStatus.SUPPLIER_ASSIGNED,
	EProductsBySupplierStatus.SUPPLIERS_TO_ASSIGN,
	EProductsBySupplierStatus.OFFER_UPLOADED,
]

function resolveStatusFields(data: IProductWithSuppliers, selectedSuppliers: ISelectedSupplierModel[]) {
	return {
		isProcessed: (data.processed?.length ?? 0) > 0,
		isSupplierLocked: (data.processed ?? []).some(
			entry => !entry.status || !SUPPLIER_EDITABLE_STATUSES.includes(entry.status),
		),
		isRemoved: (data.removed?.length ?? 0) > 0,
		selectedSupplierRecord: selectedSuppliers.length ? selectedSuppliers[0] : null,
		removedRecord: data.removed?.length ? ProductStatus(data.removed[0]) : null,
		processedRecord: data.processed?.length ? ProductStatus(data.processed[0]) : null,
	}
}

function buildOrderBreakdownItem(item: any) {
	return {
		orderId: item.purchase_order_id ?? null,
		orderName: item.name ?? '',
		requiredQuantity: item.required_quantity ?? 0,
		unit: item.unit ?? '',
	}
}

function buildPurchaseOrderItemUpdate(item: IPurchaseOrderItemUpdate): IPurchaseOrderItemUpdateModel {
	return {
		id: item.id,
		productionSimulationVersion: item.production_simulation_version,
		previousRequiredQuantity: item.previous_required_quantity,
		requiredQuantity: item.required_quantity,
		unit: item.unit,
		createdAt: item.created_at,
	}
}

function resolveProductHistoryFields(data: IProductWithSuppliers) {
	return {
		orderBreakdown: Array.isArray(data.order_breakdown) ? data.order_breakdown.map(buildOrderBreakdownItem) : [],
		updates: Array.isArray(data.updates) ? data.updates.map(buildPurchaseOrderItemUpdate) : [],
	}
}

export const ProductWithSuppliers = (data: IProductWithSuppliers): IProductWithSuppliersModel => {
	const {
		raw_material_id,
		supplier_product_name,
		selected_supplier,
		supplier,
		unit_price,
		supplier_name,
		category_label,
		id,
		name,
		source,
		category,
		quantity,
		unit,
	} = data

	const supplierData = supplier ?? selected_supplier ?? []
	const selectedSuppliers = supplierData?.map(s => SelectedSupplier(s)) ?? []
	const firstSelectedSupplier = selectedSuppliers[0]
	const normalizedSupplierProductName = supplier_product_name ?? firstSelectedSupplier?.productName ?? ''
	const supplierProductName =
		firstSelectedSupplier?.source === 'manual' && normalizedSupplierProductName
			? `[Manual] ${normalizedSupplierProductName}`
			: normalizedSupplierProductName

	return {
		id,
		name,
		source,
		supplierProductName,
		quantity,
		unit,
		isManual: source === 'manual',
		categoryLabel: category_label ?? category ?? '-',
		unitPrice: unit_price ?? null,
		rawMaterialId: raw_material_id,
		quantityValid: false,
		supplierName: resolveProductSupplierName(supplier_name, firstSelectedSupplier),
		selectedSupplier: selectedSuppliers,
		availableSuppliers: resolveProductAvailableSuppliers(data),
		...resolveProductHistoryFields(data),
		...resolveProductBaseFields(data),
		...resolveCollectionFields(data),
		...resolveStatusFields(data, selectedSuppliers),
	}
}

export const ProductsBySupplierSupplier = (data: IProductsBySupplierSupplier): IProductsBySupplierSupplierModel => {
	const { logistic_minimum_amount, logistic_minimum_unit, supplier_order_number, ...rest } = data

	return {
		...rest,
		logisticMinimumAmount: logistic_minimum_amount,
		logisticMinimumUnit: logistic_minimum_unit,
		supplierOrderNumber: supplier_order_number,
	}
}

export const RawMaterialSupplier = (data: IRawMaterialSupplier): IRawMaterialSupplierModel => {
	const {
		supplier_product_id,
		supplier_price_list_id,
		supplier_product_name,
		unit_price,
		crm_price_per_kg,
		package_content_net,
		active_price_list,
		...rest
	} = data

	return {
		...rest,
		supplierProductId: supplier_product_id,
		supplierPriceListId: supplier_price_list_id,
		supplierProductName: supplier_product_name,
		unitPrice: unit_price,
		crmPricePerKg: crm_price_per_kg ?? null,
		packageContentNet: package_content_net,
		activePriceList: typeof active_price_list === 'boolean' ? active_price_list : Boolean(active_price_list),
	}
}

export const AsignSupplierToJson = (data: IAsignSupplierModel): IAsignSupplier => {
	const { productId, supplierId, amount } = data

	return {
		product_id: productId,
		supplier_id: supplierId,
		amount,
	}
}

export const SupplierLogisticDataFromProductsBySupplier = (
	supplier: IProductsBySupplierSupplierModel,
): ISupplierLogisticData => ({
	id: supplier.id,
	name: supplier.name,
	totalAmount: 0,
	logisticMinimumAmount: supplier.logisticMinimumAmount ?? null,
	logisticMinimumUnit: supplier.logisticMinimumUnit ?? 'zł',
})

export const SupplierLogisticDataFromSelectedSupplier = (
	selectedSupplier: ISelectedSupplierModel,
	availableSupplier?: IAvailableSupplierModel,
	logisticMinimum?: { amount: number; unit: string },
): ISupplierLogisticData => ({
	id: selectedSupplier.supplierId ?? selectedSupplier.id,
	name: selectedSupplier.supplierName ?? selectedSupplier.name ?? '-',
	totalAmount: 0,
	logisticMinimumAmount:
		selectedSupplier.logisticMinimumAmount ??
		logisticMinimum?.amount ??
		availableSupplier?.logisticMinimumAmount ??
		null,
	logisticMinimumUnit:
		selectedSupplier.logisticMinimumUnit ?? logisticMinimum?.unit ?? availableSupplier?.logisticMinimumUnit ?? 'zł',
})

export const SupplierLogisticDataFromProductsBySupplierWithTotal = (
	supplier: IProductsBySupplierSupplierModel,
	totalAmount: number,
): ISupplierLogisticData => ({
	totalAmount,
	id: supplier.id,
	name: supplier.name,
	logisticMinimumAmount: supplier.logisticMinimumAmount ?? null,
	logisticMinimumUnit: supplier.logisticMinimumUnit ?? 'zł',
})

export const SupplierLogisticInfo = (data: ISupplierLogisticData): ISupplierLogisticInfo => ({
	id: data.id,
	name: data.name,
	currentAmount: data.totalAmount > 0 ? formatPriceGlobal(data.totalAmount) : '0.00 zł',
	logisticMinimum:
		data.logisticMinimumAmount !== null && data.logisticMinimumAmount !== undefined
			? formatPriceGlobal(data.logisticMinimumAmount)
			: '-',
	currentAmountValue: data.totalAmount,
	logisticMinimumValue: data.logisticMinimumAmount,
})

export const createLoadingProduct = (
	tempProductId: number,
	rawMaterialId: number,
	name: string,
): IProductWithSuppliersModel => ({
	name,
	rawMaterialId,
	id: tempProductId,
	source: 'unknown',
	isManual: false,
	systemName: name,
	supplierProductName: '',
	mainName: '',
	isAlternative: false,
	category: '',
	categoryLabel: '-',
	quantity: 0,
	requiredQuantity: 0,
	orderedQuantity: 0,
	quantityValid: false,
	unit: '',
	unitPrice: null,
	packageContentNet: null,
	supplierName: '-',
	pricePerKg: null,
	netValue: 0,
	deliveryDeadline: null,
	selectedSupplier: [],
	availableSuppliers: [],
	logisticMinimumAmount: null,
	logisticMinimumUnit: null,
	removed: [],
	processed: [],
	warningCodes: [],
	warningMessages: [],
	isProcessed: false,
	isSupplierLocked: false,
	isRemoved: false,
	selectedSupplierRecord: null,
	removedRecord: null,
	processedRecord: null,
	warehouseStock: null,
	orderBreakdown: [],
	updates: [],
})

export const AlternativeProductResponse = (data: IAlternativeProductResponse): IAlternativeProductResponseModel => {
	const {
		created_at,
		is_alternative,
		main_name,
		main_raw_material_id,
		purchase_order_id,
		raw_material_id,
		updated_at,
		...rest
	} = data

	return {
		...rest,
		createdAt: created_at,
		isAlternative: is_alternative,
		mainName: main_name,
		mainRawMaterialId: main_raw_material_id,
		purchaseOrderId: purchase_order_id,
		rawMaterialId: raw_material_id,
		updatedAt: updated_at,
	}
}

export const RestoredProductResponse = (data: IRestoredProductResponse): IRestoredProductResponseModel => {
	const {
		created_at,
		is_alternative,
		main_name,
		main_raw_material_id,
		purchase_order_id,
		raw_material_id,
		updated_at,
		...rest
	} = data

	return {
		...rest,
		createdAt: created_at,
		isAlternative: is_alternative,
		mainName: main_name,
		mainRawMaterialId: main_raw_material_id,
		purchaseOrderId: purchase_order_id,
		rawMaterialId: raw_material_id,
		updatedAt: updated_at,
	}
}
